import React from 'react';
import { BellRing, CheckCircle2, Flame } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export default function CheckoutDemoPreviewCard({
  serviceType,
  demoDeliveryMeta,
  customerName,
  demoServiceLabel,
  demoEffectiveAddress,
  total,
  orderNotesTrim,
}) {
  return (
    <Card className="p-5 sm:p-6 border-border bg-card text-card-foreground shadow-sm">
      <div className="space-y-5">
        <div>
          <p className="text-lg font-semibold tracking-tight text-foreground">
            Así recibiría el pedido un local usando SoluQR
          </p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Tu cliente arma el carrito, elige cómo recibir y confirma; el negocio ve todo ordenado y listo para
            operar.
          </p>
          {serviceType === 'delivery' ? (
            <p className="mt-3 inline-flex items-center rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-medium text-foreground">
              Tiempo estimado: {demoDeliveryMeta.eta}
            </p>
          ) : null}
        </div>

        <div className="relative">
          <div className="hidden sm:block absolute top-6 left-0 right-0 h-0.5 bg-border" aria-hidden />
          <ol className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2 relative">
            {[
              { step: 1, label: 'Pedido recibido', sub: 'Notificación al local', Icon: BellRing, status: 'done' },
              { step: 2, label: 'Cocina preparando', sub: 'Prioridad y tiempos', Icon: Flame, status: 'active' },
              { step: 3, label: 'Pedido listo', sub: 'Cliente avisado', Icon: CheckCircle2, status: 'pending' },
            ].map(({ step, label, sub, Icon, status }) => (
              <li key={step} className="flex sm:flex-col items-start sm:items-center gap-3 sm:text-center">
                <div
                  className={cn(
                    'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border shadow-sm',
                    status === 'done' && 'bg-muted text-foreground border-border',
                    status === 'pending' && 'bg-muted/50 text-muted-foreground border-border',
                    status === 'active' && 'border-primary bg-primary text-primary-foreground shadow-md'
                  )}
                >
                  <Icon className="w-5 h-5 relative" />
                </div>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-sm font-semibold',
                      status === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                    )}
                  >
                    {label}
                  </p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Resumen para el local
          </p>
          <ul className="text-sm text-foreground space-y-1.5">
            <li>
              <span className="text-muted-foreground">Cliente:</span>{' '}
              {customerName.trim() || 'Cliente demo'}
            </li>
            <li>
              <span className="text-muted-foreground">Entrega:</span> {demoServiceLabel}
            </li>
            {serviceType === 'delivery' ? (
              <li>
                <span className="text-muted-foreground">Dirección:</span> {demoEffectiveAddress}
              </li>
            ) : null}
            <li>
              <span className="text-muted-foreground">Total:</span>{' '}
              <span className="font-semibold tabular-nums">${total.toLocaleString('es-CL')}</span>
            </li>
            {orderNotesTrim ? (
              <li>
                <span className="text-muted-foreground">Notas:</span> {orderNotesTrim}
              </li>
            ) : (
              <li className="text-muted-foreground text-xs">
                Notas: — (el cliente puede agregar indicaciones)
              </li>
            )}
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm font-semibold text-foreground mb-2">Qué ve el negocio</p>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-4 marker:text-primary">
            <li>Pedido organizado por ítems y cantidades</li>
            <li>Productos claros (incluye agregados y observaciones)</li>
            <li>Datos de contacto del cliente cuando corresponde</li>
            <li>Canal WhatsApp u operación interna según la configuración del local</li>
          </ul>
        </div>
      </div>
    </Card>
  );
}
